import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';

const Section = ({ title, children }) => (
    <div className="mb-8">
        <h2 className="text-lg font-semibold text-slate-900 mb-3">{title}</h2>
        <div className="text-slate-600 leading-7 space-y-3">{children}</div>
    </div>
);

const PrivacyPolicy = () => {
    useEffect(() => { window.scrollTo(0, 0); }, []);

    return (
        <div className="h-screen overflow-y-auto bg-slate-50">
            {/* Header */}
            <header className="bg-white border-b border-slate-200 sticky top-0 z-10">
                <div className="max-w-3xl mx-auto px-6 py-4 flex items-center justify-between">
                    <Link to="/" className="flex items-center gap-2 hover:opacity-80 transition-opacity">
                        <span className="text-[17px] font-bold tracking-tight text-slate-900">Career Champ</span>
                    </Link>
                    <Link to="/login" className="text-sm font-medium text-[#3F57E0] hover:underline">
                        Back to login
                    </Link>
                </div>
            </header>

            <main className="max-w-3xl mx-auto px-6 py-12">
                {/* Title */}
                <div className="mb-10">
                    <h1 className="text-3xl font-bold text-slate-900 tracking-tight mb-2">Privacy Policy</h1>
                    <p className="text-sm text-slate-400">Last updated: May 2026</p>
                </div>

                <Section title="1. Introduction">
                    <p>
                        Career Champ ("we", "us", or "our") is committed to protecting your personal information. This Privacy Policy explains what data we collect, how we use it, and your rights regarding your data.
                    </p>
                    <p>
                        By using Career Champ, you agree to the collection and use of information in accordance with this policy.
                    </p>
                </Section>

                <Section title="2. Information We Collect">
                    <p><strong className="text-slate-800">Account information:</strong> Name, email address, password (hashed), and profile photo when you register.</p>
                    <p><strong className="text-slate-800">Google account data:</strong> If you sign in with Google, we receive your name, email, and profile photo. With your permission, we store Google OAuth tokens to send emails on your behalf via the Gmail API.</p>
                    <p><strong className="text-slate-800">LinkedIn account data:</strong> If you connect LinkedIn via our integration, we store an account identifier and session token to perform actions (sending connection requests, messages) on your behalf.</p>
                    <p><strong className="text-slate-800">Resume / CV data:</strong> If you upload a CV, we parse and store its contents (work history, education, skills) to personalise your experience.</p>
                    <p><strong className="text-slate-800">Usage data:</strong> Pages visited, features used, search queries, and actions taken within the platform.</p>
                    <p><strong className="text-slate-800">Payment data:</strong> Billing is handled by Stripe. We do not store your card details. We receive and store subscription status and payment history.</p>
                </Section>

                <Section title="3. How We Use Your Information">
                    <ul className="list-disc pl-5 space-y-1">
                        <li>To provide and personalise the Service (job matching, recruiter suggestions, hiring signals)</li>
                        <li>To generate AI-powered outreach messages and strategy advice using your profile</li>
                        <li>To send transactional emails (OTP verification, password reset, account notifications)</li>
                        <li>To process payments and manage your subscription</li>
                        <li>To connect your LinkedIn account and perform actions you authorise</li>
                        <li>To improve the platform through anonymised usage analytics</li>
                        <li>To comply with legal obligations</li>
                    </ul>
                </Section>

                <Section title="4. Third-Party Services">
                    <p>To operate the Service, we work with trusted third-party providers in the following categories:</p>
                    <ul className="list-disc pl-5 space-y-1">
                        <li>Payment processing</li>
                        <li>Authentication (Google OAuth)</li>
                        <li>LinkedIn account integration</li>
                        <li>AI-powered content generation and analysis</li>
                        <li>Cloud database and application hosting</li>
                    </ul>
                    <p>Each provider processes only the data necessary to perform their function. We select providers that maintain appropriate security and privacy standards.</p>
                    <p>We do not sell your personal data to third parties.</p>
                </Section>

                <Section title="5. Data Retention">
                    <p>
                        We retain your account data for as long as your account is active. If you delete your account, we will remove your personal data within 30 days, except where we are required to retain it for legal or billing purposes.
                    </p>
                    <p>
                        Anonymised or aggregated data may be retained indefinitely for analytics purposes.
                    </p>
                </Section>

                <Section title="6. Cookies and Local Storage">
                    <p>
                        Career Champ uses browser local storage to maintain your login session (JWT token) and cache certain preferences. We do not use third-party tracking cookies.
                    </p>
                </Section>

                <Section title="7. Data Security">
                    <p>
                        We take reasonable technical and organisational measures to protect your data, including encrypted connections (HTTPS), hashed passwords, and access controls. However, no method of transmission over the internet is 100% secure.
                    </p>
                </Section>

                <Section title="8. Your Rights">
                    <p>Depending on your location, you may have the following rights:</p>
                    <ul className="list-disc pl-5 space-y-1">
                        <li><strong className="text-slate-800">Access</strong> — Request a copy of the personal data we hold about you</li>
                        <li><strong className="text-slate-800">Correction</strong> — Request correction of inaccurate data</li>
                        <li><strong className="text-slate-800">Deletion</strong> — Request deletion of your account and personal data</li>
                        <li><strong className="text-slate-800">Portability</strong> — Request your data in a portable format</li>
                        <li><strong className="text-slate-800">Objection</strong> — Object to certain types of processing</li>
                    </ul>
                    <p>
                        To exercise any of these rights, contact us at{' '}
                        <a href="mailto:info@careerchamp.io" className="text-[#3F57E0] hover:underline">
                            info@careerchamp.io
                        </a>
                    </p>
                </Section>

                <Section title="9. Children's Privacy">
                    <p>
                        Career Champ is not directed at children under the age of 8. We do not knowingly collect personal data from children. If you believe a child has provided us with personal data, please contact us and we will delete it promptly.
                    </p>
                </Section>

                <Section title="10. International Users">
                    <p>
                        Career Champ is operated from the United States. If you access the Service from outside the US, your data may be transferred to and processed in the US or other countries where our service providers operate. By using the Service, you consent to this transfer.
                    </p>
                </Section>

                <Section title="11. Changes to This Policy">
                    <p>
                        We may update this Privacy Policy from time to time. We will notify you of material changes via email or an in-app notice. The "Last updated" date at the top of this page indicates when the policy was last revised.
                    </p>
                </Section>

                <Section title="12. Contact Us">
                    <p>
                        If you have any questions or concerns about this Privacy Policy, please contact us at:
                    </p>
                    <div className="bg-slate-100 rounded-xl p-4 text-sm">
                        <p className="font-semibold text-slate-800">Career Champ</p>
                        <p>Email: <a href="mailto:info@careerchamp.io" className="text-[#3F57E0] hover:underline">info@careerchamp.io</a></p>
                        <p>Website: <a href="https://careerchamp.io" className="text-[#3F57E0] hover:underline">careerchamp.io</a></p>
                    </div>
                </Section>

                {/* Footer links */}
                <div className="mt-12 pt-6 border-t border-slate-200 flex gap-6 text-sm text-slate-500">
                    <Link to="/terms" className="hover:text-[#3F57E0] hover:underline">Terms of Service</Link>
                    <Link to="/login" className="hover:text-[#3F57E0] hover:underline">Back to login</Link>
                </div>
            </main>
        </div>
    );
};

export default PrivacyPolicy;
